class ShotPdp extends HTMLElement {
  #abortController = new AbortController();
  #galleryFrame = 0;
  #galleryIndex = 0;
  #mainActionObserver;
  #submitObserver;

  connectedCallback() {
    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('change', this.#handleChange, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('submit', this.#handleSubmit, { capture: true, signal });
    this.addEventListener('variant:update', this.#handleVariantUpdate, { signal });
    document.addEventListener('cart:add', this.#handleCartComplete, { signal });
    document.addEventListener('cart:error', this.#handleCartComplete, { signal });

    const viewport = this.querySelector('[data-shot-gallery-viewport]');
    viewport?.addEventListener('scroll', this.#handleGalleryScroll, { passive: true, signal });
    const relatedTrack = this.querySelector('[data-shot-related-track]');
    relatedTrack?.addEventListener('scroll', this.#updateRelatedControls, { passive: true, signal });
    window.addEventListener('resize', this.#updateRelatedControls, { passive: true, signal });
    document.addEventListener('focusin', this.#keepFocusVisible, { signal });

    this.#galleryIndex = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.#syncPurchaseState();
    this.#setupStickyAtc();
    this.#updateRelatedControls();
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#mainActionObserver?.disconnect();
    this.#submitObserver?.disconnect();
    if (this.#galleryFrame) cancelAnimationFrame(this.#galleryFrame);
  }

  get slides() {
    return Array.from(this.querySelectorAll('[data-shot-slide]'));
  }

  #handleClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-shot-reviews-link]')) {
      const reviewWidget = Array.from(document.querySelectorAll('.yotpo-widget-instance')).find((widget) => {
        const rect = widget.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });
      const reviews = reviewWidget?.closest('.shopify-section') || reviewWidget;
      reviews?.scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'start'
      });
      return;
    }

    const gallerySelect = target.closest('[data-shot-gallery-select]');
    if (gallerySelect) {
      this.#selectGalleryItem(Number(gallerySelect.dataset.shotGallerySelect));
      return;
    }

    if (target.closest('[data-shot-gallery-previous]')) {
      this.#selectGalleryItem(this.#galleryIndex - 1);
      return;
    }

    if (target.closest('[data-shot-gallery-next]')) {
      this.#selectGalleryItem(this.#galleryIndex + 1);
      return;
    }

    const relatedButton = target.closest('[data-shot-related-previous], [data-shot-related-next]');
    if (relatedButton) {
      const track = this.querySelector('[data-shot-related-track]');
      const card = track?.querySelector('.shot-related__card');
      if (!(track instanceof HTMLElement) || !(card instanceof HTMLElement)) return;
      const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
      const direction = relatedButton.matches('[data-shot-related-previous]') ? -1 : 1;
      track.scrollBy({
        left: direction * (card.getBoundingClientRect().width + gap),
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
      return;
    }

    const tab = target.closest('[role="tab"]');
    if (tab && tab.closest('[data-shot-tabs]')) {
      this.#selectTab(tab, true);
      return;
    }

    if (target.closest('[data-shot-sticky-submit]')) {
      const submit = this.querySelector('[data-shot-main-actions] [ref="addToCartButton"]');
      if (submit instanceof HTMLButtonElement && !submit.disabled) submit.click();
    }
  };

  #handleChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement || target instanceof HTMLSelectElement)) return;

    if (target instanceof HTMLInputElement && target.matches('.shot-pack-picker input[type="radio"]')) {
      this.#previewVariant(target);
      return;
    }

    if (target.name === 'purchase_type' || target.name === 'selling_plan') {
      this.#syncPurchaseState();
    }
  };

  #handleSubmit = async (event) => {
    if (!(event.target instanceof HTMLFormElement) || !event.target.matches('[data-type="add-to-cart-form"]')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!event.target.reportValidity()) return;

    this.#setLoadingState(true);

    const form = event.target;
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    const errorRegion = this.querySelector('[ref="addToCartTextError"]');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item to cart.');

      if (errorRegion) {
        errorRegion.textContent = '';
        errorRegion.classList.add('hidden');
      }
      if (liveRegion) liveRegion.textContent = 'Added to cart';

      document.dispatchEvent(new CustomEvent('cart:add', {
        detail: { source: 'shot-pdp', data, resource: data.items?.[0] || data },
      }));
      if (window.loadCart) await window.loadCart();
      this.#syncCartSubscriptionMessage();
      window.openCartDrawer?.();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to add this item to cart.';
      if (errorRegion) {
        errorRegion.textContent = message;
        errorRegion.classList.remove('hidden');
      }
      if (liveRegion) liveRegion.textContent = message;
      document.dispatchEvent(new CustomEvent('cart:error', {
        detail: { source: 'shot-pdp', error: message },
      }));
    } finally {
      this.#setLoadingState(false);
    }
  };

  #handleCartComplete = (event) => {
    if (!this.hasAttribute('data-shot-loading')) return;
    this.#setLoadingState(false);
  };

  #handleKeydown = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    if (target.matches('[data-shot-gallery-viewport]')) {
      if (window.innerWidth >= 1280) return;
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this.#selectGalleryItem(this.#galleryIndex - 1);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        this.#selectGalleryItem(this.#galleryIndex + 1);
      }
      return;
    }

    if (!target.matches('[role="tab"]') || !target.closest('[data-shot-tabs]')) return;

    const tabs = Array.from(target.closest('[role="tablist"]')?.querySelectorAll('[role="tab"]') || []);
    const currentIndex = tabs.indexOf(target);
    if (currentIndex < 0) return;

    let nextIndex;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % tabs.length;
    if (event.key === 'ArrowLeft') nextIndex = (currentIndex - 1 + tabs.length) % tabs.length;
    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = tabs.length - 1;
    if (nextIndex === undefined) return;

    event.preventDefault();
    event.stopPropagation();
    this.#selectTab(tabs[nextIndex], true);
  };

  #handleVariantUpdate = (event) => {
    if (String(event.detail?.data?.productId) !== this.dataset.productId) return;

    const variantId = this.querySelector('input[name="id"]');
    if (variantId instanceof HTMLInputElement) variantId.value = event.detail?.resource?.id || '';

    const submit = this.querySelector('[data-shot-main-actions] [ref="addToCartButton"]');
    if (submit instanceof HTMLButtonElement) submit.disabled = event.detail?.resource?.available !== true;

    const source = event.detail?.data?.html?.querySelector(
      `shot-pdp[data-product-id="${this.dataset.productId}"] [data-shot-purchase-options]`
    );
    const current = this.querySelector('[data-shot-purchase-options]');
    const previousType = current?.querySelector('input[name="purchase_type"]:checked')?.value;

    if (source && current) {
      current.innerHTML = source.innerHTML;
      const preserved = current.querySelector(`input[name="purchase_type"][value="${previousType}"]`);
      if (preserved instanceof HTMLInputElement) preserved.checked = true;
    }

    const mediaId = event.detail?.resource?.featured_media?.id;
    const mediaIndex = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
    if (mediaIndex >= 0) this.#selectGalleryItem(mediaIndex);

    this.#syncPurchaseState();
    this.#syncStickyAtc();
  };

  #previewVariant(input) {
    const variantId = this.querySelector('input[name="id"]');
    if (variantId instanceof HTMLInputElement) variantId.value = input.dataset.variantId || '';

    const isAvailable = input.dataset.shotVariantAvailable === 'true';
    const submit = this.querySelector('[data-shot-main-actions] [ref="addToCartButton"]');
    if (submit instanceof HTMLButtonElement) submit.disabled = !isAvailable;

    const oneTimeOption = this.querySelector('[data-shot-one-time-option]');
    const oneTimePrice = oneTimeOption?.querySelector('[data-shot-one-time-price]');
    const oneTimeUnitPrice = oneTimeOption?.querySelector('[data-shot-one-time-unit-price]');
    if (oneTimePrice) oneTimePrice.textContent = input.dataset.shotOneTimePrice || 'Unavailable';
    if (oneTimeUnitPrice) oneTimeUnitPrice.textContent = input.dataset.shotOneTimeUnitPrice || '';

    const subscriptionOption = this.querySelector('[data-shot-subscription-option]');
    const subscriptionAvailable = input.dataset.shotSubscriptionAvailable === 'true';
    if (subscriptionOption instanceof HTMLElement) subscriptionOption.hidden = !subscriptionAvailable;

    const terms = this.querySelector('[data-shot-terms]');
    if (terms instanceof HTMLElement) terms.hidden = !subscriptionAvailable;

    const sellingPlan = this.querySelector('select[name="selling_plan"]');
    const selectedPlan = sellingPlan?.selectedOptions?.[0];
    if (selectedPlan instanceof HTMLOptionElement && subscriptionAvailable) {
      selectedPlan.dataset.shotFirstPrice = input.dataset.shotSubscriptionPrice || '';
      selectedPlan.dataset.shotPerBottle = input.dataset.shotSubscriptionUnitPrice || '';
      selectedPlan.dataset.shotFirstDiscount = input.dataset.shotFirstDiscount || '';
      selectedPlan.dataset.shotRecurringPrice = input.dataset.shotRecurringPrice || '';
      selectedPlan.dataset.shotRecurringDiscount = input.dataset.shotRecurringDiscount || '';
    }

    if (!subscriptionAvailable) {
      const oneTimeInput = this.querySelector('input[name="purchase_type"][value="one-time"]');
      if (oneTimeInput instanceof HTMLInputElement) oneTimeInput.checked = true;
    }

    const mediaIndex = this.slides.findIndex((slide) => String(slide.dataset.mediaId) === String(input.dataset.mediaId));
    if (mediaIndex >= 0) this.#selectGalleryItem(mediaIndex);

    this.#syncPurchaseState();
    this.#syncStickyAtc();
    this.#announcePackSelection(input, subscriptionAvailable);
  }

  #announcePackSelection(input, subscriptionAvailable) {
    const liveRegion = this.querySelector('[ref="liveRegion"]');
    if (!liveRegion) return;

    const packLabel = input.closest('label')?.querySelector('span')?.textContent.trim() || input.value;
    const withUnitPrice = (price, unitPrice) => (unitPrice ? `${price} (${unitPrice})` : price);
    const parts = [`${packLabel} selected.`];
    if (input.dataset.shotVariantAvailable !== 'true') parts.push('Sold out.');
    if (subscriptionAvailable && input.dataset.shotSubscriptionPrice) {
      parts.push(`Subscribe and save: ${withUnitPrice(input.dataset.shotSubscriptionPrice, input.dataset.shotSubscriptionUnitPrice)}.`);
    }
    parts.push(`One-time purchase: ${withUnitPrice(input.dataset.shotOneTimePrice || 'Unavailable', input.dataset.shotOneTimeUnitPrice)}.`);

    liveRegion.textContent = parts.join(' ');
  }

  #handleGalleryScroll = () => {
    if (this.#galleryFrame) return;
    this.#galleryFrame = requestAnimationFrame(() => {
      this.#galleryFrame = 0;
      const viewport = this.querySelector('[data-shot-gallery-viewport]');
      if (!viewport) return;

      const viewportRect = viewport.getBoundingClientRect();
      let nearestIndex = this.#galleryIndex;
      let nearestDistance = Number.POSITIVE_INFINITY;
      this.slides.forEach((slide, index) => {
        const rect = slide.getBoundingClientRect();
        const distance = Math.abs(rect.left - viewportRect.left) + Math.abs(rect.top - viewportRect.top);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }
      });

      if (nearestIndex !== this.#galleryIndex) {
        this.#galleryIndex = nearestIndex;
      }
    });
  };

  #selectGalleryItem(index) {
    const slides = this.slides;
    if (!slides.length) return;

    const normalizedIndex = (index + slides.length) % slides.length;
    this.#galleryIndex = normalizedIndex;
    slides[0].parentElement?.classList.toggle('has-active-overflow', normalizedIndex >= 5);
    slides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === normalizedIndex;
      slide.classList.toggle('is-active', isActive);
      slide.querySelector('[data-shot-gallery-select]')?.setAttribute('aria-current', String(isActive));
    });

    if (window.innerWidth < 1280) {
      slides[normalizedIndex].scrollIntoView({
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
        block: 'nearest',
        inline: 'start'
      });
    }
  }

  #selectTab(tab, moveFocus) {
    const tablist = tab.closest('[role="tablist"]');
    if (!tablist) return;

    for (const candidate of tablist.querySelectorAll('[role="tab"]')) {
      const selected = candidate === tab;
      candidate.setAttribute('aria-selected', String(selected));
      candidate.tabIndex = selected ? 0 : -1;
      const panelId = candidate.getAttribute('aria-controls');
      const panel = panelId ? this.querySelector(`#${panelId}`) : null;
      if (panel) panel.hidden = !selected;
    }

    const ingredients = tab.closest('.shot-ingredients');
    if (ingredients instanceof HTMLElement) {
      ingredients.style.setProperty('--formula-color', tab.dataset.formulaColor || '');
      ingredients.style.setProperty('--shot-accent-text', tab.dataset.formulaInk || '');
      ingredients.style.setProperty('--formula-tab-ink', tab.dataset.formulaTabInk || '#000');
      const heading = ingredients.querySelector('[data-formula-heading]');
      if (heading) heading.textContent = tab.dataset.formulaName || tab.textContent.trim();
    }

    if (moveFocus) tab.focus();
  }

  #syncPurchaseState() {
    const selectedType = this.querySelector('input[name="purchase_type"]:checked')?.value;
    const sellingPlan = this.querySelector('select[name="selling_plan"]');
    if (sellingPlan instanceof HTMLSelectElement) sellingPlan.disabled = selectedType !== 'subscription';

    const selectedPlan = sellingPlan?.selectedOptions?.[0];
    if (selectedPlan instanceof HTMLOptionElement) {
      const saving = this.querySelector('[data-shot-saving]');
      const terms = this.querySelector('[data-shot-terms]');
      const selectedPrice = this.querySelector('[data-shot-selected-price]');
      const selectedUnitPrice = this.querySelector('[data-shot-selected-unit-price]');
      if (saving) saving.textContent = `${selectedPlan.dataset.shotFirstDiscount}% off***`;
      if (terms) {
        terms.textContent = `${selectedPlan.dataset.shotFirstDiscount}% OFF your first subscription order. Then ${selectedPlan.dataset.shotRecurringPrice} (${selectedPlan.dataset.shotRecurringDiscount}% off) every delivery after. T&C apply***`;
      }
      if (selectedPrice) selectedPrice.textContent = selectedPlan.dataset.shotFirstPrice || '';
      if (selectedUnitPrice) selectedUnitPrice.textContent = selectedPlan.dataset.shotPerBottle || '';
    }

    for (const option of this.querySelectorAll('.shot-purchase__option')) {
      const input = option.querySelector('input[name="purchase_type"]');
      option.classList.toggle('is-muted', input instanceof HTMLInputElement && !input.checked);
    }

    this.#syncStickyAtc();
  }

  #syncCartSubscriptionMessage() {
    const selectedType = this.querySelector('input[name="purchase_type"]:checked')?.value;
    const selectedPlan = this.querySelector('select[name="selling_plan"]')?.selectedOptions?.[0];
    const message = document.querySelector('#cart-drawer .cart-drawer-main__content > p');
    if (selectedType !== 'subscription' || !(selectedPlan instanceof HTMLOptionElement) || !message) return;

    message.textContent = `${selectedPlan.dataset.shotFirstDiscount}% OFF your first subscription order. Then ${selectedPlan.dataset.shotRecurringPrice} (${selectedPlan.dataset.shotRecurringDiscount}% off) every delivery after. T&C apply***`;
  }

  #setLoadingState(isLoading) {
    this.toggleAttribute('data-shot-loading', isLoading);
    const buttons = [
      ...this.querySelectorAll('[ref="addToCartButton"], [data-shot-sticky-submit]'),
      ...document.querySelectorAll('[data-shot-subscription-atc]')
    ];

    for (const button of buttons) {
      if (!(button instanceof HTMLButtonElement)) continue;
      const label = button.querySelector('.add-to-cart-text__content') || button;
      if (isLoading) {
        button.dataset.shotWasDisabled = String(button.disabled);
        label.dataset.shotOriginalLabel = label.textContent.trim();
        label.textContent = 'Adding…';
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
      } else {
        if (label.dataset.shotOriginalLabel) label.textContent = label.dataset.shotOriginalLabel;
        button.disabled = button.dataset.shotWasDisabled === 'true';
        button.removeAttribute('aria-busy');
        delete button.dataset.shotWasDisabled;
        delete label.dataset.shotOriginalLabel;
      }
    }
  }

  #updateRelatedControls = () => {
    const track = this.querySelector('[data-shot-related-track]');
    const previous = this.querySelector('[data-shot-related-previous]');
    const next = this.querySelector('[data-shot-related-next]');
    if (!(track instanceof HTMLElement) || !(previous instanceof HTMLButtonElement) || !(next instanceof HTMLButtonElement)) return;
    const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
    previous.disabled = track.scrollLeft <= 1;
    next.disabled = track.scrollLeft >= maximumScroll - 1;
  };

  #setupStickyAtc() {
    const sticky = this.querySelector('[data-shot-sticky]');
    const actions = this.querySelector('[data-shot-main-actions]');
    const submit = actions?.querySelector('[ref="addToCartButton"]');
    if (!sticky || !actions || !(submit instanceof HTMLButtonElement)) return;

    this.#mainActionObserver = new IntersectionObserver(([entry]) => {
      const isMobile = window.matchMedia('(max-width: 989px)').matches;
      const passedAboveViewport = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      sticky.hidden = !(isMobile && passedAboveViewport);
      if (!sticky.hidden) this.#syncStickyAtc();
    });
    this.#mainActionObserver.observe(actions);

    this.#submitObserver = new MutationObserver(this.#syncStickyAtc);
    this.#submitObserver.observe(submit, { attributes: true, childList: true, subtree: true, characterData: true });
  }

  #syncStickyAtc = () => {
    const sticky = this.querySelector('[data-shot-sticky]');
    const stickyButton = sticky?.querySelector('[data-shot-sticky-submit]');
    const stickyPrice = sticky?.querySelector('[data-shot-sticky-price]');
    const submit = this.querySelector('[data-shot-main-actions] [ref="addToCartButton"]');
    const submitLabel = submit?.querySelector('.add-to-cart-text__content');
    const selectedPrice = this.querySelector('input[name="purchase_type"]:checked')
      ?.closest('.shot-purchase__option')
      ?.querySelector('.shot-purchase__price strong');

    if (stickyButton instanceof HTMLButtonElement && submit instanceof HTMLButtonElement) {
      stickyButton.disabled = submit.disabled;
      stickyButton.textContent = submitLabel?.textContent.trim() || submit.textContent.trim();
    }
    if (stickyPrice && selectedPrice) stickyPrice.textContent = selectedPrice.textContent.trim();
  };

  #keepFocusVisible = (event) => {
    const sticky = this.querySelector('[data-shot-sticky]');
    const target = event.target;
    if (!sticky || sticky.hidden || !(target instanceof HTMLElement)) return;
    if (sticky.contains(target) || target.closest('dialog[open]')) return;
    if (!target.matches(':focus-visible')) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const focusedRect = target.getBoundingClientRect();
        const stickyRect = sticky.getBoundingClientRect();
        const clearance = 24;
        if (focusedRect.bottom > stickyRect.top - clearance) {
          window.scrollBy({ top: focusedRect.bottom - stickyRect.top + clearance, behavior: 'auto' });
        }
      });
    });
  };
}

if (!customElements.get('shot-pdp')) customElements.define('shot-pdp', ShotPdp);

const selectShotIngredientTab = (tab, moveFocus = false) => {
  const ingredients = tab.closest('.shot-ingredients[data-shot-tabs]');
  const tablist = tab.closest('[role="tablist"]');
  if (!(ingredients instanceof HTMLElement) || !tablist) return;

  for (const candidate of tablist.querySelectorAll('[role="tab"]')) {
    const selected = candidate === tab;
    candidate.setAttribute('aria-selected', String(selected));
    candidate.tabIndex = selected ? 0 : -1;
    const panelId = candidate.getAttribute('aria-controls');
    const panel = panelId ? ingredients.querySelector(`#${panelId}`) : null;
    if (panel) panel.hidden = !selected;
  }

  ingredients.style.setProperty('--formula-color', tab.dataset.formulaColor || '');
  ingredients.style.setProperty('--shot-accent-text', tab.dataset.formulaInk || '');
  ingredients.style.setProperty('--formula-tab-ink', tab.dataset.formulaTabInk || '#000');
  const heading = ingredients.querySelector('[data-formula-heading]');
  if (heading) heading.textContent = tab.dataset.formulaName || tab.textContent.trim();
  if (moveFocus) tab.focus();
};

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;

  const ingredientTab = event.target.closest('.shot-ingredients[data-shot-tabs] [role="tab"]');
  if (ingredientTab) {
    selectShotIngredientTab(ingredientTab);
    return;
  }

  const subscriptionButton = event.target.closest('[data-shot-subscription-atc]');
  if (subscriptionButton) {
    if (subscriptionButton instanceof HTMLButtonElement && subscriptionButton.disabled) return;
    const shotPdp = document.querySelector('[data-shot-pdp]');
    const subscriptionRadio = shotPdp?.querySelector('input[name="purchase_type"][value="subscription"]');
    const productForm = shotPdp?.querySelector('form[data-type="add-to-cart-form"]');

    if (subscriptionRadio instanceof HTMLInputElement && !subscriptionRadio.checked) {
      subscriptionRadio.checked = true;
      subscriptionRadio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    if (productForm instanceof HTMLFormElement) productForm.requestSubmit();
    return;
  }

  const comparisonButton = event.target.closest('[data-shot-comparison-prev], [data-shot-comparison-next]');
  if (comparisonButton) {
    const comparison = comparisonButton.closest('[data-shot-comparison]');
    const scroller = comparison?.querySelector('[data-shot-comparison-scroller]');
    if (!(scroller instanceof HTMLElement)) return;

    const direction = comparisonButton.matches('[data-shot-comparison-prev]') ? -1 : 1;
    scroller.scrollBy({ left: direction * 104, behavior: 'smooth' });
    return;
  }

  const recommendationButton = event.target.closest('[data-shot-recommendations-prev], [data-shot-recommendations-next]');
  if (!recommendationButton) return;

  const recommendations = recommendationButton.closest('[data-shot-recommendations]');
  const track = recommendations?.querySelector('.shot-recommendations__track');
  if (!(track instanceof HTMLElement)) return;

  const direction = recommendationButton.matches('[data-shot-recommendations-prev]') ? -1 : 1;
  track.scrollBy({ left: direction * 189, behavior: 'smooth' });
});

document.addEventListener('keydown', (event) => {
  const tab = event.target instanceof Element
    ? event.target.closest('.shot-ingredients[data-shot-tabs] [role="tab"]')
    : null;
  if (!tab || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
  const tabs = [...tab.closest('[role="tablist"]').querySelectorAll('[role="tab"]')];
  const index = tabs.indexOf(tab);
  if (index < 0) return;
  const nextIndex = event.key === 'Home'
    ? 0
    : event.key === 'End'
      ? tabs.length - 1
      : (index + (event.key === 'ArrowRight' ? 1 : -1) + tabs.length) % tabs.length;
  event.preventDefault();
  selectShotIngredientTab(tabs[nextIndex], true);
});

const initializeShotRecommendations = (root = document) => {
  root.querySelectorAll('[data-shot-recommendations]').forEach((recommendations) => {
    const track = recommendations.querySelector('.shot-recommendations__track');
    const controls = recommendations.querySelector('.shot-recommendations__controls');
    const previousButton = recommendations.querySelector('[data-shot-recommendations-prev]');
    const nextButton = recommendations.querySelector('[data-shot-recommendations-next]');
    if (!(track instanceof HTMLElement) || !(controls instanceof HTMLElement) || !(previousButton instanceof HTMLButtonElement) || !(nextButton instanceof HTMLButtonElement)) return;
    if (recommendations.dataset.carouselReady === 'true') return;

    const updateControls = () => {
      const maximumScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const hasOverflow = maximumScroll > 1;
      controls.hidden = !hasOverflow;
      track.tabIndex = hasOverflow ? 0 : -1;
      previousButton.disabled = track.scrollLeft <= 1;
      nextButton.disabled = track.scrollLeft >= maximumScroll - 1;
    };

    recommendations.dataset.carouselReady = 'true';
    track.addEventListener('scroll', updateControls, { passive: true });
    window.addEventListener('resize', updateControls, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updateControls).observe(track);
    updateControls();
  });
};

const initializeShotMomentsScrollbars = (root = document) => {
  root.querySelectorAll('[data-shot-moments-viewport]').forEach((viewport) => {
    if (!(viewport instanceof HTMLElement) || viewport.dataset.scrollCueReady === 'true') return;
    const section = viewport.closest('.shot-discovery-moments');
    const cue = section?.querySelector('[data-shot-moments-scroll-cue]');
    const thumb = cue?.querySelector('[data-shot-moments-scroll-thumb]');
    if (!(cue instanceof HTMLElement) || !(thumb instanceof HTMLElement)) return;

    const updateCue = () => {
      const maximumScroll = Math.max(0, viewport.scrollWidth - viewport.clientWidth);
      const maximumOffset = Math.max(0, cue.clientWidth - thumb.clientWidth);
      const progress = maximumScroll ? viewport.scrollLeft / maximumScroll : 0;
      thumb.style.setProperty('--shot-moments-thumb-offset', `${maximumOffset * progress}px`);
      cue.hidden = maximumScroll < 1;
    };

    viewport.dataset.scrollCueReady = 'true';
    viewport.addEventListener('scroll', updateCue, { passive: true });
    window.addEventListener('resize', updateCue, { passive: true });
    if ('ResizeObserver' in window) new ResizeObserver(updateCue).observe(viewport);
    updateCue();
  });
};

initializeShotRecommendations();
initializeShotMomentsScrollbars();
document.addEventListener('shopify:section:load', (event) => {
  initializeShotRecommendations(event.target);
  initializeShotMomentsScrollbars(event.target);
});
